import React, { useMemo, useState } from "react";
import { Box, Button, Card, TablePagination, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import QueryStatsOutlinedIcon from "@mui/icons-material/QueryStatsOutlined";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import RuleOutlinedIcon from "@mui/icons-material/RuleOutlined";
import styles from "./zeroTrustPolicyDetail.module.scss";
import ZeroTrustKpiCard from "./ZeroTrustKpiCard";
import ComplianceBreadcrumb from "../compliance/ComplianceBreadcrumb";
import { TopCard } from "../phishing/PhishingDetail";
import EditPolicyDialog from "./EditPolicyDialog";
import ConfirmationDialog from "components/shared/Dialog/ConfirmationDialog";

const policyDetail = {
  name: "Block External OT Access",
  status: "Active",
  type: "Denial",
  description:
    "This policy prevents any external network access to operational technology devices and systems. It ensures that only authorized internal systems can communicate with OT infrastructure.",
  created: "2024-11-15",
  lastModified: "2024-12-20",
  owner: "Security Team",
  metrics: {
    protectedAssets: 156,
    totalEvents: 847,
    blockedToday: 23,
    activeRules: 12,
    priority: "High",
  },
  configuration: {
    scope: "OT Network",
    status: "Active",
    enforcementMode: "Automatic",
    policyId: "POL-0001",
  },
  rules: [
    {
      id: 1,
      name: "Block External IP Ranges",
      condition: "Source IP NOT IN [10.0.0.0/8, 172.16.0.0/12, 192.168.0.0/16]",
      action: "DENY",
      priority: 1,
      triggers: 342,
    },
    {
      id: 2,
      name: "Deny Internet Gateway Access",
      condition: "Destination Gateway == Internet",
      action: "DENY",
      priority: 1,
      triggers: 189,
    },
    {
      id: 3,
      name: "Block Foreign Countries",
      condition: "GeoIP NOT IN [US, CA]",
      action: "DENY + ALERT",
      priority: 1,
      triggers: 156,
    },
    {
      id: 4,
      name: "Prevent VPN Tunneling",
      condition: "Protocol IN [OpenVPN, WireGuard, IPSec]",
      action: "DENY + LOG",
      priority: 2,
      triggers: 78,
    },
    {
      id: 5,
      name: "Restrict Remote Desktop",
      condition: "Port IN [3389, 5900, 5901]",
      action: "DENY",
      priority: 2,
      triggers: 52,
    },
    {
      id: 6,
      name: "Block SSH from External",
      condition: "Port == 22 AND Source External",
      action: "DENY + ALERT",
      priority: 1,
      triggers: 30,
    },
  ],
  recentEvents: [
    {
      timestamp: "2024-12-30 14:23:15",
      sourceIp: "203.0.113.45",
      targetAsset: "PLC-ZONE-A-01",
      rule: "Block External IP Ranges",
    },
    {
      timestamp: "2024-12-30 12:18:42",
      sourceIp: "198.51.100.22",
      targetAsset: "SCADA-MAIN-HMI",
      rule: "Block Foreign Countries",
    },
    {
      timestamp: "2024-12-30 09:45:33",
      sourceIp: "192.0.2.178",
      targetAsset: "RTU-SUBSTATION-03",
      rule: "Prevent VPN Tunneling",
    },
    {
      timestamp: "2024-12-30 08:12:09",
      sourceIp: "203.0.113.89",
      targetAsset: "DCS-CONTROL-02",
      rule: "Block SSH from External",
    },
    {
      timestamp: "2024-12-29 23:56:44",
      sourceIp: "198.51.100.111",
      targetAsset: "ICS-HISTORIAN",
      rule: "Restrict Remote Desktop",
    },
    {
      timestamp: "2024-12-29 20:34:12",
      sourceIp: "203.0.113.156",
      targetAsset: "PLC-ZONE-B-04",
      rule: "Block External IP Ranges",
    },
    {
      timestamp: "2024-12-29 18:22:55",
      sourceIp: "192.0.2.90",
      targetAsset: "SCADA-BACKUP",
      rule: "Deny Internet Gateway Access",
    },
  ],
};

const kpiCards = [
  {
    label: "Protected Assets",
    value: policyDetail.metrics.protectedAssets,
    helper: "Devices under policy",
    icon: <ShieldOutlinedIcon fontSize="small" />,
    iconBg: "#e0f2fe",
    iconColor: "#2563eb",
  },
  {
    label: "Total Events",
    value: policyDetail.metrics.totalEvents,
    helper: "All time triggers",
    icon: <QueryStatsOutlinedIcon fontSize="small" />,
    iconBg: "#d1fae5",
    iconColor: "#059669",
  },
  {
    label: "Blocked Today",
    value: policyDetail.metrics.blockedToday,
    helper: "Denied attempts",
    icon: <BlockOutlinedIcon fontSize="small" />,
    iconBg: "#fee2e2",
    iconColor: "#dc2626",
  },
  {
    label: "Active Rules",
    value: policyDetail.metrics.activeRules,
    helper: "Policy rules",
    icon: <RuleOutlinedIcon fontSize="small" />,
    iconBg: "#ede9fe",
    iconColor: "#7c3aed",
  },
];

const ZeroTrustPolicyDetail = () => {
  const navigate = useNavigate();
  const [rulesPage, setRulesPage] = useState(0);
  const [rulesRowsPerPage, setRulesRowsPerPage] = useState(5);
  const [eventsPage, setEventsPage] = useState(0);
  const [eventsRowsPerPage, setEventsRowsPerPage] = useState(5);
  const [editPolicyOpen, setEditPolicyOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const editablePolicy = useMemo(
    () => ({
      name: policyDetail.name,
      type: policyDetail.type,
      scope: policyDetail.configuration.scope,
      description: policyDetail.description,
      status: policyDetail.status,
      enforcement: policyDetail.configuration.enforcementMode,
      priority: policyDetail.metrics.priority,
      owner: policyDetail.owner,
    }),
    []
  );

  const pagedRules = useMemo(() => {
    const start = rulesPage * rulesRowsPerPage;
    return policyDetail.rules.slice(start, start + rulesRowsPerPage);
  }, [rulesPage, rulesRowsPerPage]);

  const pagedEvents = useMemo(() => {
    const start = eventsPage * eventsRowsPerPage;
    return policyDetail.recentEvents.slice(start, start + eventsRowsPerPage);
  }, [eventsPage, eventsRowsPerPage]);

  const handleDeleteConfirm = () => {
    setDeleteOpen(false);
    navigate("/zero-trust-policies");
  };

  return (
    <Box className={styles.page}>
      <Box className={styles.headerRow}>
        <ComplianceBreadcrumb
          items={[
            { label: "Threats & Incidents", href: "/zero-trust-policies" },
            { label: policyDetail.name },
          ]}
        />

        <Box className={styles.headerActions}>
          <Button
            variant="outlined"
            startIcon={<EditOutlinedIcon />}
            className={styles.editButton}
            onClick={() => setEditPolicyOpen(true)}
          >
            Edit
          </Button>
          <Button
            variant="outlined"
            startIcon={<DeleteOutlineOutlinedIcon />}
            className={styles.deleteButton}
            onClick={() => setDeleteOpen(true)}
          >
            Delete
          </Button>
        </Box>
      </Box>
      <TopCard
        title={policyDetail.name}
        severity={policyDetail.status}
        description={policyDetail.description}
      />

      <Box className={styles.kpiGrid}>
        {kpiCards.map((card) => (
          <ZeroTrustKpiCard
            key={card.label}
            label={card.label}
            value={card.value}
            helper={card.helper}
            icon={card.icon}
            iconBg={card.iconBg}
            iconColor={card.iconColor}
          />
        ))}
      </Box>

      <Card className={styles.sectionCard}>
        <Typography className={styles.sectionTitle}>
          Policy Configuration
        </Typography>
        <Box className={styles.configGrid}>
          <Box className={styles.configItem}>
            <Typography className={styles.configLabel}>Scope</Typography>
            <Typography className={styles.configValue}>
              {policyDetail.configuration.scope}
            </Typography>
          </Box>
          <Box className={styles.configItem}>
            <Typography className={styles.configLabel}>Status</Typography>
            <Box className={styles.configStatus}>
              <span className={styles.statusDot} />
              <Typography className={styles.configValue}>
                {policyDetail.configuration.status}
              </Typography>
            </Box>
          </Box>
          <Box className={styles.configItem}>
            <Typography className={styles.configLabel}>
              Enforcement Mode
            </Typography>
            <Typography className={styles.configValue}>
              {policyDetail.configuration.enforcementMode}
            </Typography>
          </Box>
          <Box className={styles.configItem}>
            <Typography className={styles.configLabel}>Policy ID</Typography>
            <span className={styles.policyId}>
              {policyDetail.configuration.policyId}
            </span>
          </Box>
        </Box>
      </Card>

      <Card className={styles.tableCard}>
        <Typography
          className={styles.sectionTitle}
          sx={{ p: 1, px: 2, fontWeight: "bold" }}
        >
          Policy Rules ({policyDetail.rules.length})
        </Typography>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>#</th>
              <th>Rule Name</th>
              <th>Condition</th>
              <th>Action</th>
              <th>Priority</th>
              <th>Triggers</th>
            </tr>
          </thead>
          <tbody>
            {pagedRules.map((rule) => (
              <tr key={rule.id}>
                <td>
                  <span className={styles.ruleIndex}>{rule.id}</span>
                </td>
                <td className={styles.ruleName}>{rule.name}</td>
                <td>
                  <span className={styles.ruleCondition}>{rule.condition}</span>
                </td>
                <td>
                  <span className={styles.ruleAction}>{rule.action}</span>
                </td>
                <td className={styles.rulePriority}>{rule.priority}</td>
                <td className={styles.ruleTriggers}>{rule.triggers}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <TablePagination
          component="div"
          count={policyDetail.rules.length}
          page={rulesPage}
          onPageChange={(_, page) => setRulesPage(page)}
          rowsPerPage={rulesRowsPerPage}
          onRowsPerPageChange={(event) => {
            setRulesRowsPerPage(parseInt(event.target.value, 10));
            setRulesPage(0);
          }}
          rowsPerPageOptions={[5, 10]}
        />
      </Card>

      <Card className={styles.tableCard}>
        <Typography
          className={styles.sectionTitle}
          sx={{ p: 1, px: 2, fontWeight: "bold" }}
        >
          Recent Events
        </Typography>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Timestamp</th>
              <th>Source IP</th>
              <th>Target Asset</th>
              <th>Triggered Rule</th>
            </tr>
          </thead>
          <tbody>
            {pagedEvents.map((event) => (
              <tr key={`${event.timestamp}-${event.sourceIp}`}>
                <td className={styles.eventTimestamp}>{event.timestamp}</td>
                <td>
                  <span className={styles.eventIp}>{event.sourceIp}</span>
                </td>
                <td className={styles.eventAsset}>{event.targetAsset}</td>
                <td className={styles.eventRule}>{event.rule}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <TablePagination
          component="div"
          count={policyDetail.recentEvents.length}
          page={eventsPage}
          onPageChange={(_, page) => setEventsPage(page)}
          rowsPerPage={eventsRowsPerPage}
          onRowsPerPageChange={(event) => {
            setEventsRowsPerPage(parseInt(event.target.value, 10));
            setEventsPage(0);
          }}
          rowsPerPageOptions={[5, 10]}
        />
      </Card>
      <EditPolicyDialog
        open={editPolicyOpen}
        policy={editablePolicy}
        onClose={() => setEditPolicyOpen(false)}
      />
      <ConfirmationDialog
        open={deleteOpen}
        title="Delete Policy"
        description={`Are you sure you want to delete "${policyDetail.name}"? This action cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={handleDeleteConfirm}
        onClose={() => setDeleteOpen(false)}
      />
    </Box>
  );
};

export default ZeroTrustPolicyDetail;
